/**
 * BillAttachmentsCard — interactive attachments list + uploader.
 *
 * Two-phase upload (mirrors the avatar flow):
 *   1. `bills.attachments.requestUpload` → server returns presigned URL + key
 *   2. `fetch(url, { method: 'PUT', body: file })` direct-to-S3
 *   3. `bills.attachments.confirm` with the same key + file metadata
 *
 * The component is gated on `billId`. Until the bill is saved as a draft, we
 * show a "Save the bill first" placeholder — there's no row to attach files
 * against yet. Once the parent provides a `billId`, the uploader activates
 * and the list refetches from the server.
 *
 * Errors:
 *   - `S3 not configured` (PRECONDITION_FAILED) — the API server's storage
 *     client is the disabled stub. Surfaces as a banner so dev can see it.
 *   - Type / size validation runs client-side BEFORE `requestUpload` so the
 *     server doesn't waste a presign on a known-bad file.
 *
 * @see apps/api/src/routers/bills.ts (attachments sub-router)
 * @see packages/domain/src/purchases/bill-attachments/
 */
import { useId, useRef, useState } from "react";
import { FileImage, FileText, Loader2, Lock, Plus, Sparkles, Trash2, X } from "lucide-react";
import { Alert, Button } from "@flint/ui";
import { trpc } from "../../../lib/trpc.js";
import { BillAttachmentViewer } from "../components/bill-attachment-viewer.js";

/**
 * Keep these aligned with `ALLOWED_ATTACHMENT_TYPES` /
 * `ATTACHMENT_MAX_BYTES` in `packages/domain/src/purchases/bill-attachments/types.ts`.
 * The server re-validates on `requestUpload` and again on `confirm`.
 */
const ACCEPT = "application/pdf,image/jpeg,image/png,image/webp";
const ACCEPT_TYPES = new Set(["application/pdf", "image/jpeg", "image/png", "image/webp"]);
const MAX_BYTES = 25 * 1024 * 1024;

interface BillAttachmentsCardProps {
  /** Bill id once the draft has been saved. `null` disables the uploader. */
  billId: string | null;
}

export function BillAttachmentsCard({ billId }: BillAttachmentsCardProps) {
  const inputId = useId();
  const inputRef = useRef<HTMLInputElement>(null);
  const utils = trpc.useUtils();

  const list = trpc.bills.attachments.list.useQuery(
    { billId: billId ?? "" },
    { enabled: billId !== null },
  );

  const requestUpload = trpc.bills.attachments.requestUpload.useMutation();
  const confirm = trpc.bills.attachments.confirm.useMutation({
    onSuccess: async () => {
      if (billId) await utils.bills.attachments.list.invalidate({ billId });
    },
  });
  const remove = trpc.bills.attachments.remove.useMutation({
    onSuccess: async () => {
      if (billId) await utils.bills.attachments.list.invalidate({ billId });
    },
  });

  // Local upload state — one entry per file currently mid-flight so we can
  // show a progress strip with per-file error/success.
  const [inflight, setInflight] = useState<UploadProgress[]>([]);
  const [validationError, setValidationError] = useState<string | null>(null);
  const [storageDisabled, setStorageDisabled] = useState(false);

  // Inline viewer state — opening a row preview, closing returns to the list.
  // We keep the whole attachment object so the viewer doesn't need its own
  // round-trip to fetch metadata.
  const [viewing, setViewing] = useState<{
    name: string;
    contentType: string;
    publicUrl: string;
    sizeBytes: string;
  } | null>(null);

  // No bill id yet — show the gated placeholder.
  if (!billId) {
    return (
      <div className="rounded-md border border-dashed border-line-2 bg-raised p-5 text-sm">
        <div className="flex items-center gap-2 text-ink-2">
          <Lock className="h-4 w-4 text-muted" />
          <p className="font-semibold">Save the bill first to attach files.</p>
        </div>
        <p className="mt-1.5 text-xs text-muted">
          Use <span className="font-semibold text-ink-2">Save draft</span> below — once saved you
          can upload PDFs and images (max 25 MB each, up to 4 types).
        </p>
      </div>
    );
  }

  const items = list.data?.items ?? [];

  const onFilesPicked = async (files: FileList | File[]) => {
    setValidationError(null);
    const fileList = Array.from(files);
    for (const file of fileList) {
      const error = validateFile(file);
      if (error) {
        setValidationError(error);
        continue;
      }
      void uploadOne(file);
    }
  };

  /** Run the three-step pipeline for a single file. */
  const uploadOne = async (file: File) => {
    const progressId = `${file.name}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    setInflight((prev) => [...prev, { id: progressId, file, state: "requesting" }]);

    try {
      // 1. Ask the server for a presigned URL.
      const req = await requestUpload.mutateAsync({
        billId,
        originalName: file.name,
        contentType: file.type as AcceptType,
        sizeBytes: file.size,
      });

      setInflight((prev) =>
        prev.map((p) => (p.id === progressId ? { ...p, state: "uploading" } : p)),
      );

      // 2. Direct PUT to S3. We can't easily progress-bar fetch() here;
      // XMLHttpRequest is the only XHR that exposes `progress` events. Keep
      // it minimal for V1 — a spinner per row covers the common case.
      const putResponse = await fetch(req.uploadUrl, {
        method: "PUT",
        headers: { "Content-Type": file.type },
        body: file,
      });
      if (!putResponse.ok) {
        throw new Error(
          `S3 PUT failed: ${putResponse.status} ${putResponse.statusText || "no message"}`,
        );
      }

      setInflight((prev) =>
        prev.map((p) => (p.id === progressId ? { ...p, state: "confirming" } : p)),
      );

      // 3. Confirm — server inserts the row + audits.
      await confirm.mutateAsync({
        billId,
        objectKey: req.objectKey,
        originalName: file.name,
        contentType: file.type as AcceptType,
        sizeBytes: file.size,
      });

      // Done — remove from inflight; the list query refetched via onSuccess.
      setInflight((prev) => prev.filter((p) => p.id !== progressId));
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      // S3 not configured surfaces here from requestUpload; flag the banner.
      if (message.includes("uploads are disabled")) {
        setStorageDisabled(true);
      }
      setInflight((prev) =>
        prev.map((p) => (p.id === progressId ? { ...p, state: "error", message } : p)),
      );
    }
  };

  return (
    <div className="grid min-w-0 gap-2">
      {storageDisabled ? (
        <Alert
          intent="warn"
          title="Uploads disabled in this environment"
          description="The API server doesn't have S3 credentials configured. Set FLINT_S3_ACCESS_KEY_ID + FLINT_S3_SECRET_ACCESS_KEY to enable."
        />
      ) : null}
      {validationError ? (
        <Alert intent="bad" title="Couldn't add that file" description={validationError} />
      ) : null}

      {/* List */}
      {list.isLoading ? (
        <p className="text-sm text-muted">Loading attachments…</p>
      ) : list.error ? (
        <Alert intent="bad" title="Couldn't load attachments" description={list.error.message} />
      ) : items.length === 0 && inflight.length === 0 ? (
        <p className="rounded-md border border-dashed border-line-2 bg-paper p-4 text-center text-sm text-muted">
          No attachments yet.
        </p>
      ) : null}

      {/*
        Scrollable list. Caps at three rows tall (~204px including the
        gap-2 between rows + a half-row peek so the 4th file is visibly
        scrollable) — prevents a many-attachment bill from pushing the
        composer/print buttons off-screen on the rail. `pr-1` reserves
        a hair of room so the scrollbar doesn't clip the row's hover
        outline. The wrapper is rendered when ANY row exists (persisted
        or inflight); empty-state stays above.
       */}
      <div className="grid max-h-[13.5rem] gap-2 overflow-y-auto pr-1">
        {items.map((item) => (
          <AttachmentRow
            key={item.id}
            contentType={item.contentType}
            name={item.originalName}
            meta={`${formatBytes(item.sizeBytes)} · uploaded ${formatRelative(item.uploadedAt)}`}
            publicUrl={item.publicUrl}
            onOpen={
              item.publicUrl
                ? () =>
                    setViewing({
                      name: item.originalName,
                      contentType: item.contentType,
                      publicUrl: item.publicUrl!,
                      sizeBytes: item.sizeBytes,
                    })
                : undefined
            }
            onRemove={() =>
              remove.mutate(
                { id: item.id },
                {
                  onError: () => {
                    /* shown via the row's tooltip / state — TODO V1.x */
                  },
                },
              )
            }
            removing={remove.isPending && remove.variables?.id === item.id}
          />
        ))}

        {/* Inflight rows — shown while uploading; vanish on success. */}
        {inflight.map((p) => (
          <PendingRow key={p.id} progress={p} />
        ))}
      </div>

      <input
        ref={inputRef}
        id={inputId}
        type="file"
        multiple
        accept={ACCEPT}
        className="sr-only"
        onChange={(event) => {
          if (event.target.files) void onFilesPicked(event.target.files);
          // Reset so the same file can be picked again after remove + re-add.
          event.target.value = "";
        }}
      />
      <Button
        type="button"
        intent="ghost"
        size="sm"
        className="mt-1 w-full min-w-0 border border-dashed border-line-2 text-sky-700"
        onClick={() => inputRef.current?.click()}
      >
        <Plus className="h-4 w-4" />
        Add attachment
      </Button>
      <p
        className="min-w-0 truncate text-xs text-muted"
        title="PDF, JPEG, PNG, or WebP. Max 25 MB per file."
      >
        <Sparkles className="-mt-0.5 mr-1 inline h-3 w-3 text-sky-700" />
        PDF, JPEG, PNG, or WebP. Max 25 MB per file.
      </p>

      <BillAttachmentViewer
        open={viewing !== null}
        onOpenChange={(open) => {
          if (!open) setViewing(null);
        }}
        attachment={viewing}
      />
    </div>
  );
}

// ── inflight row ────────────────────────────────────────────────────

interface UploadProgress {
  id: string;
  file: File;
  state: "requesting" | "uploading" | "confirming" | "error";
  message?: string;
}

function PendingRow({ progress }: { progress: UploadProgress }) {
  const stateLabel: Record<UploadProgress["state"], string> = {
    requesting: "Requesting upload…",
    uploading: "Uploading…",
    confirming: "Confirming…",
    error: progress.message ?? "Upload failed",
  };
  const isError = progress.state === "error";
  return (
    <div
      className={`flex min-w-0 items-center gap-3 rounded-md border p-3 ${
        isError ? "border-bad-100 bg-bad-50" : "border-line bg-raised"
      }`}
    >
      <span
        className={`grid h-9 w-9 shrink-0 place-items-center rounded-sm ${
          isError ? "bg-bad-50 text-bad" : "bg-sky-50 text-sky-700"
        }`}
      >
        {isError ? <X className="h-4 w-4" /> : <Loader2 className="h-4 w-4 animate-spin" />}
      </span>
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-semibold text-ink" title={progress.file.name}>
          {progress.file.name}
        </p>
        <p className={`truncate text-xs ${isError ? "text-bad" : "text-muted"}`}>
          {stateLabel[progress.state]}
        </p>
      </div>
    </div>
  );
}

// ── persisted row ───────────────────────────────────────────────────

interface AttachmentRowProps {
  contentType: string;
  name: string;
  meta: string;
  publicUrl: string | null;
  /** Click handler for opening the inline viewer. Undefined when no
   *  publicUrl is available (the row is then non-interactive). */
  onOpen: (() => void) | undefined;
  onRemove: () => void;
  removing: boolean;
}

function AttachmentRow({
  contentType,
  name,
  meta,
  publicUrl,
  onOpen,
  onRemove,
  removing,
}: AttachmentRowProps) {
  const isImage = contentType.startsWith("image/");
  const Icon = isImage ? FileImage : FileText;
  const interactive = Boolean(publicUrl && onOpen);
  return (
    <div
      role={interactive ? "button" : undefined}
      tabIndex={interactive ? 0 : undefined}
      onClick={interactive ? onOpen : undefined}
      onKeyDown={
        interactive
          ? (e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                onOpen?.();
              }
            }
          : undefined
      }
      className={`flex min-w-0 items-center gap-3 rounded-md border border-line bg-raised p-3 ${
        interactive
          ? "cursor-pointer transition-colors hover:bg-paper-2 focus:outline-none focus-visible:ring-2 focus-visible:ring-sky-700"
          : ""
      }`}
      aria-label={interactive ? `Preview ${name}` : undefined}
    >
      <span
        className={`grid h-9 w-9 shrink-0 place-items-center rounded-sm ${
          isImage ? "bg-sky-50 text-sky-700" : "bg-bad-50 text-bad"
        }`}
      >
        <Icon className="h-4 w-4" />
      </span>
      <div className="min-w-0 flex-1 overflow-hidden">
        <p
          className={`truncate text-sm font-semibold text-ink ${interactive ? "hover:text-sky-700" : ""}`}
          title={name}
        >
          {name}
        </p>
        <p className="truncate text-xs text-muted" title={meta}>
          {meta}
        </p>
      </div>
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          onRemove();
        }}
        disabled={removing}
        className="grid h-8 w-8 shrink-0 place-items-center rounded-sm text-muted hover:bg-bad-50 hover:text-bad disabled:opacity-40"
        aria-label={`Remove ${name}`}
      >
        {removing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
      </button>
    </div>
  );
}

// ── helpers ─────────────────────────────────────────────────────────

type AcceptType = "application/pdf" | "image/jpeg" | "image/png" | "image/webp";

function validateFile(file: File): string | null {
  if (!ACCEPT_TYPES.has(file.type)) {
    return `"${file.name}" has unsupported type "${file.type || "unknown"}". Use PDF, JPEG, PNG, or WebP.`;
  }
  if (file.size > MAX_BYTES) {
    return `"${file.name}" is ${formatBytes(BigInt(file.size))}; max 25 MB.`;
  }
  if (file.size === 0) {
    return `"${file.name}" is empty.`;
  }
  return null;
}

function formatBytes(bytes: bigint | string): string {
  const n = typeof bytes === "string" ? Number(bytes) : Number(bytes);
  if (!Number.isFinite(n) || n <= 0) return "0 B";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

function formatRelative(iso: string): string {
  const then = new Date(iso).getTime();
  const seconds = Math.max(1, Math.round((Date.now() - then) / 1000));
  if (seconds < 60) return "just now";
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.round(hours / 24);
  return `${days}d ago`;
}
